import { Service } from '@quality24/inpatient-typings';
import { useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Button, Modal, Text } from '@quality24/design-system';
import { useHTMLParser } from '@quality24/react-hooks';
import { isEqual } from 'lodash';
import { ChargeableItemFragment } from '@/inpatient-patient-pwa/types/global';
import useI18nResolvers from '@/inpatient-patient-pwa/i18n/useI18nResolvers';
import MultipleChoiceWithQuantity, {
  Choice,
} from '../MultipleChoiceWithQuantity';

export interface Props {
  className?: string;
  show: boolean;
  reason: Required<Pick<Service, 'id' | 'modifier'>> & {
    chargeableItems: Array<ChargeableItemFragment>;
  };
  onHide: () => void;
  onComplete: (orderChargeableItems: Record<string, number>) => void;
}

export interface ChargeableItemsFormSchema {
  orderChargeableItems: Record<string, number>;
}

const getValidationSchema = (t: TFunction) =>
  Yup.object().shape({
    orderChargeableItems: Yup.object()
      .test(
        'is-valid-object',
        t('validation.selectAtLeastOneItem'),
        (value) => {
          if (!value || typeof value !== 'object' || isEqual(value, {}))
            return false;
          return Object.values(value).every((v) => typeof v === 'number');
        },
      )
      .required(),
  });

const OrderChargeableItemsModal: React.FunctionComponent<Props> = ({
  className,
  show,
  reason,
  onHide,
  onComplete,
}) => {
  const { t } = useTranslation();
  const { resolveI18n } = useI18nResolvers();
  const htmlParser = useHTMLParser();
  const validationSchema = React.useMemo(() => getValidationSchema(t), [t]);

  const title =
    resolveI18n(reason.modifier?.titleI18N) ||
    reason.modifier?.title ||
    t('order.fillBelow');

  const description =
    resolveI18n(reason.modifier?.descriptionI18N) ||
    reason.modifier?.description;

  const choices = React.useMemo<Choice[]>(
    () =>
      reason.chargeableItems.map((ci) => ({
        value: ci.id,
        title: ci.nameI18N,
        description: ci.descriptionI18N || '',
        image: ci.config.image || undefined,
        min: ci.limit.minQuantity,
        max: ci.limit.maxQuantity,
      })),
    [reason.chargeableItems],
  );

  const formik = useFormik<ChargeableItemsFormSchema>({
    initialValues: {
      orderChargeableItems: {},
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: ({ orderChargeableItems }) => {
      onComplete(orderChargeableItems);
    },
  });

  return (
    <Modal
      id="order-chargeable-items-modal"
      className={className}
      show={show}
      onHide={onHide}
      title={title}
      footer={
        <Button
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid}
          fluid
          size="lg"
        >
          {t('common.save')}
        </Button>
      }
    >
      {description && (
        <Text
          dangerouslySetInnerHTML={{
            __html: htmlParser(description),
          }}
        />
      )}
      <div className="p-1">
        <MultipleChoiceWithQuantity
          choices={choices}
          value={formik.values.orderChargeableItems}
          onChange={(quantity, item) => {
            formik.setFieldValue('orderChargeableItems', {
              ...formik.values.orderChargeableItems,
              [item]: quantity,
            });
          }}
        />
      </div>
    </Modal>
  );
};

export default OrderChargeableItemsModal;
